/**
 * AlertNotifier — fires an in-app toast whenever the unread-alert count rises.
 *
 * Mounted once in the AppShell so notifications surface on every page. The
 * Sidebar badge reads the same query cache; only this component owns the
 * "count went up → toast" side effect to avoid duplicate toasts.
 */
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@shared/constants'
import { useToast } from '@/hooks/useToast'
import { useAlertsUnread } from '@/hooks/useAlertsUnread'

export function AlertNotifier() {
  const { data: count } = useAlertsUnread()
  const { toast } = useToast()
  const navigate = useNavigate()
  const prev = useRef<number | null>(null)

  useEffect(() => {
    if (count == null) return
    const previous = prev.current
    prev.current = count
    // Skip the first load — only notify on a genuine increase.
    if (previous == null || count <= previous) return

    const delta = count - previous
    toast.warning(delta === 1 ? 'New alert triggered' : `${delta} new alerts triggered`, {
      description: 'Negative customer sentiment needs your attention.',
      duration: 8000,
      action: { label: 'View', onClick: () => navigate(ROUTES.ALERTS) },
    })
  }, [count, toast, navigate])

  return null
}
